import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  type InfiniteData,
  type UseMutationOptions,
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { useAuthBearer } from "../hooks/useAuthBearer";

type QueryOptions<T> = Omit<UseQueryOptions<T, Error>, "queryKey" | "queryFn">;

type InfiniteQueryOptions<TPage> = Omit<
  UseInfiniteQueryOptions<
    TPage,
    Error,
    InfiniteData<TPage>,
    readonly string[],
    string | undefined
  >,
  "queryKey" | "queryFn"
>;
type MutationOptions<TBody, TResponse> = Omit<
  UseMutationOptions<TResponse, Error, TBody>,
  "mutationFn"
>;

// ---------------------------------------------------------------------------
// React Query hooks — Okta auth header injected automatically
// ---------------------------------------------------------------------------

/** useGet — authenticated GET. The URL is used as the query key. */
export function useGet<T>(url: string, options?: QueryOptions<T>) {
  const authHeader = useAuthBearer();
  return useQuery<T, Error>({
    ...options,
    queryKey: [url],
    queryFn: () => get<T>(url, toAuthHeaders(authHeader)),
  });
}

/** useGetInfinite — authenticated paginated GET. The URL builder receives the current page param. */
export function useGetInfinite<TPage>(
  getUrl: (pageParam: string | undefined) => string,
  queryKey: readonly string[],
  options: InfiniteQueryOptions<TPage>
) {
  const authHeader = useAuthBearer();
  return useInfiniteQuery<
    TPage,
    Error,
    InfiniteData<TPage>,
    readonly string[],
    string | undefined
  >({
    ...options,
    queryKey,
    queryFn: ({ pageParam }) =>
      get<TPage>(getUrl(pageParam), toAuthHeaders(authHeader)),
  });
}

/** usePost — authenticated POST. Pass the body as the mutate argument. */
export function usePost<TBody = unknown, TResponse = unknown>(
  url: string,
  options?: MutationOptions<TBody, TResponse>
) {
  const authHeader = useAuthBearer();
  return useMutation<TResponse, Error, TBody>({
    ...options,
    mutationFn: (body) =>
      post<TBody, TResponse>(url, body, toAuthHeaders(authHeader)),
  });
}

/** usePut — authenticated PUT. Pass the body as the mutate argument. */
export function usePut<TBody = unknown, TResponse = unknown>(
  url: string,
  options?: MutationOptions<TBody, TResponse>
) {
  const authHeader = useAuthBearer();
  return useMutation<TResponse, Error, TBody>({
    ...options,
    mutationFn: (body) =>
      put<TBody, TResponse>(url, body, toAuthHeaders(authHeader)),
  });
}

/**
 * usePatch — authenticated PATCH.
 *
 * Static URL:  usePatch<TBody, TResponse>(url, options)
 *   The mutation variable is used as the request body as-is.
 *
 * Dynamic URL: usePatch<TVariables, TResponse>((vars) => ({ url, body }), options)
 *   Use when the URL depends on the variables or the body needs transformation.
 */
export function usePatch<TVariables, TResponse>(
  request: string | ((vars: TVariables) => { url: string; body: unknown }),
  options?: MutationOptions<TVariables, TResponse>
) {
  const authHeader = useAuthBearer();
  return useMutation<TResponse, Error, TVariables>({
    ...options,
    mutationFn: (vars) => {
      if (typeof request === "string") {
        return patch<TVariables, TResponse>(
          request,
          vars,
          toAuthHeaders(authHeader)
        );
      }
      const { url, body } = request(vars);
      return patch<unknown, TResponse>(url, body, toAuthHeaders(authHeader));
    },
  });
}

/**
 * useDelete — authenticated DELETE.
 *
 * Static URL:  useDelete(url, options)
 * Dynamic URL: useDelete<TVariables>((vars) => url, options)
 *   Use when the URL depends on the mutation variable (e.g. an id).
 */
export function useDelete<TVariables = void>(
  url: string | ((vars: TVariables) => string),
  options?: MutationOptions<TVariables, void>
) {
  const authHeader = useAuthBearer();
  return useMutation<void, Error, TVariables>({
    ...options,
    mutationFn: (vars) => {
      const resolvedUrl = typeof url === "function" ? url(vars) : url;
      return del(resolvedUrl, toAuthHeaders(authHeader));
    },
  });
}

// ---------------------------------------------------------------------------
// Raw fetch utilities — exported for cases that need direct fetch control
// ---------------------------------------------------------------------------

export async function get<TResponse>(
  url: string,
  headers?: Record<string, string>
): Promise<TResponse> {
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function post<TBody, TResponse>(
  url: string,
  body: TBody,
  headers?: Record<string, string>
): Promise<TResponse> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...headers },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function put<TBody, TResponse>(
  url: string,
  body: TBody,
  headers?: Record<string, string>
): Promise<TResponse> {
  const res = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...headers },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function patch<TBody, TResponse>(
  url: string,
  body: TBody,
  headers?: Record<string, string>
): Promise<TResponse> {
  const res = await fetch(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...headers },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function del(
  url: string,
  headers?: Record<string, string>
): Promise<void> {
  const res = await fetch(url, { method: "DELETE", headers });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

function toAuthHeaders(
  authHeader: string | undefined
): Record<string, string> | undefined {
  return authHeader ? { Authorization: authHeader } : undefined;
}
